import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  Modifier,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { getEventCoordinates } from "@dnd-kit/utilities";
import {
  api,
  AppUser,
  MealSlot,
  MealType,
  NutritionStatus,
  PersonNutrition,
  RecipeListItem,
  SavedWeekItem,
  SlotCard,
  WeekDto,
  WeekShopItem,
  WeekShoppingListResponse,
  WeekSlotDto,
} from "../api/client";
import { useAuth } from "../auth";
import { RecipeModal } from "../components/RecipeModal";
import { RecipePickerSheet } from "../components/RecipePickerSheet";
import {
  DAYS,
  DAY_LABELS_FULL,
  MONTHS_CA,
  SLOT_ROWS,
  addDays,
  formatWeekRange,
  isSlotCompatible,
  isUserActiveOnSlot,
  isoMondayString,
} from "../constants";

// El destí del drop es decideix només per la posició del punter/dit.
const collisionDetection: CollisionDetection = pointerWithin;

// Centra l'overlay (la icona) sota el punter/dit.
const snapCenterToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !activatorEvent) return transform;
  const coords = getEventCoordinates(activatorEvent);
  if (!coords) return transform;
  const offsetX = coords.x - draggingNodeRect.left;
  const offsetY = coords.y - draggingNodeRect.top;
  return {
    ...transform,
    x: transform.x + offsetX - draggingNodeRect.width / 2,
    y: transform.y + offsetY - draggingNodeRect.height / 2,
  };
};

interface DragData {
  recipeId: number;
  mealType: MealType;
  cardId: number;
}

interface CardMenu {
  cardId: number;
  recipeId: number;
  baseServings: number;
  userCount: number;
  x: number;
  y: number;
}

function todayWeekday(): number {
  const d = new Date().getDay();
  return d === 0 ? 7 : d;
}

// La setmana visible es recorda entre navegacions, però només dins del mateix
// dia: l'endemà es torna a obrir amb la setmana actual.
const MONDAY_STORE_KEY = "cookmenu:weekboard-monday";

function localDayString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function loadStoredMonday(): { monday: string; selectedDay: number } | null {
  try {
    const raw = localStorage.getItem(MONDAY_STORE_KEY);
    if (!raw) return null;
    const { monday, day, selectedDay } = JSON.parse(raw);
    if (day !== localDayString() || !/^\d{4}-\d{2}-\d{2}$/.test(monday)) return null;
    return { monday, selectedDay: typeof selectedDay === "number" ? selectedDay : todayWeekday() };
  } catch {
    return null;
  }
}

function storeMonday(monday: string, selectedDay: number) {
  try {
    localStorage.setItem(MONDAY_STORE_KEY, JSON.stringify({ monday, day: localDayString(), selectedDay }));
  } catch {
    // localStorage no disponible (mode privat, etc.): simplement no es recorda.
  }
}

// Vista d'un sol dia només en mòbil vertical; apaisat i tablet mostren la graella.
function isDayView(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 720 && window.innerHeight >= window.innerWidth;
}

// Tint de la vora esquerra segons la franja (l'esmorzar comparteix tint amb breakfast).
const SLOT_TINT: Record<MealSlot, string> = {
  breakfast1: "breakfast",
  breakfast2: "breakfast",
  lunch: "lunch",
  snack: "snack",
  dinner: "dinner",
};

export function WeekBoard() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [monday, setMonday] = useState(() => loadStoredMonday()?.monday ?? isoMondayString());
  const [week, setWeek] = useState<WeekDto | null>(null);
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
  const [pickerSlot, setPickerSlot] = useState<WeekSlotDto | null>(null);
  const [menu, setMenu] = useState<CardMenu | null>(null);
  const [modal, setModal] = useState<{ recipeId: number; servings: number } | null>(null);
  const [selectedDay, setSelectedDay] = useState<number>(() => loadStoredMonday()?.selectedDay ?? todayWeekday());
  const [mobile, setMobile] = useState<boolean>(() => isDayView());
  const [actOpen, setActOpen] = useState(false);
  const [actView, setActView] = useState<"main" | "rec">("main");
  const [toast, setToast] = useState<string | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedWeeks, setSavedWeeks] = useState<SavedWeekItem[] | null>(null);
  const [pendingRestore, setPendingRestore] = useState<SavedWeekItem | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [shopItems, setShopItems] = useState<WeekShopItem[]>([]);
  const [shopMealCount, setShopMealCount] = useState(0);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopRemoved, setShopRemoved] = useState<Record<string, true>>({});
  const [toastAction, setToastAction] = useState<{ label: string; run: () => void } | null>(null);
  const [flashIds, setFlashIds] = useState<Set<number>>(new Set());
  const [filling, setFilling] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [review, setReview] = useState<WeekReviewData | null>(null);

  function closeAct() { setActOpen(false); setActView("main"); }

  async function openCompra() {
    closeAct();
    setShopRemoved({});
    setShopOpen(true);
    setShopLoading(true);
    try {
      const data = await api.get<WeekShoppingListResponse>(`/weeks/${monday}/shopping-list`);
      setShopItems(data.items);
      setShopMealCount(data.mealCount);
    } finally {
      setShopLoading(false);
    }
  }

  function removeShopItem(name: string) {
    setShopRemoved((s) => ({ ...s, [name]: true }));
    showToast(`«${name}» tret de la llista`);
  }

  async function addAllToShoppingList() {
    const remaining = shopItems.filter((it) => !shopRemoved[it.name]);
    setShopOpen(false);
    for (const it of remaining) {
      await api.post("/shopping", {
        name: it.name,
        quantity: it.quantity,
        unit: it.unit,
        ingredientId: it.ingredientId,
      });
    }
    window.dispatchEvent(new CustomEvent("cookmenu:data-changed"));
    showToast(`${remaining.length} articles afegits a la llista`);
    navigate("/compra");
  }

  function showToast(msg: string, action?: { label: string; run: () => void }) {
    setToast(msg);
    setToastAction(action ?? null);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => {
      setToast(null);
      setToastAction(null);
    }, action ? 6000 : 2600);
  }

  // Omple les franges buides visibles amb receptes triades pel servidor
  // (només on mengen usuaris) i ofereix desfer-ho des del toast.
  async function fillGaps() {
    closeAct();
    setReviewOpen(false);
    if (filling) return;
    setFilling(true);
    try {
      const res = await api.post<{ cards: { cardId: number }[] }>(`/weeks/${monday}/fill-gaps`, {
        mealSlots: SLOT_ROWS.map((r) => r.slot),
      });
      const ids = res.cards.map((c) => c.cardId);
      if (ids.length === 0) {
        showToast("No hi ha buits per omplir 🎉");
        return;
      }
      await refreshWeek();
      setFlashIds(new Set(ids));
      setTimeout(() => setFlashIds(new Set()), 1600);
      showToast(`${ids.length} àpat${ids.length > 1 ? "s" : ""} omplert${ids.length > 1 ? "s" : ""}`, {
        label: "Desfés",
        run: async () => {
          await api.post("/slot-cards/delete-many", { ids });
          await refreshWeek();
          showToast("Desfet");
        },
      });
    } catch (e: any) {
      showToast(e.message ?? "No s'han pogut omplir els buits");
    } finally {
      setFilling(false);
    }
  }

  function openReview() {
    closeAct();
    setReview(null);
    setReviewOpen(true);
    api
      .get<WeekReviewData>(`/weeks/${monday}/review`)
      .then(setReview)
      .catch(() => setReview({ weekRepeats: [], recentRepeatRecipeIds: [] }));
  }
  function printWeek() {
    closeAct();
    showToast("Preparant la impressió…");
    setTimeout(() => window.open(`/setmana/pdf?monday=${monday}`, "_blank"), 350);
  }

  function openSaveDialog() {
    closeAct();
    setSaveDialogOpen(true);
  }

  async function saveWeek(name: string): Promise<string | null> {
    try {
      await api.post<SavedWeekItem>("/saved-weeks", { name, monday });
      setSaveDialogOpen(false);
      showToast(`Setmana "${name}" desada`);
      return null;
    } catch (e: any) {
      return e.message ?? "Error en desar la setmana";
    }
  }

  function handleRestoreRequest(sw: SavedWeekItem) {
    closeAct();
    const hasCards = week?.slots.some((s) => s.cards.length > 0) ?? false;
    if (hasCards) {
      setPendingRestore(sw);
    } else {
      doRestore(sw);
    }
  }

  async function doRestore(sw: SavedWeekItem) {
    setPendingRestore(null);
    try {
      await api.post(`/weeks/${monday}/load-from-saved/${sw.id}`);
      await refreshWeek();
      showToast(`Setmana "${sw.name}" carregada`);
    } catch (e: any) {
      showToast(e.message ?? "Error en carregar la setmana");
    }
  }

  function handleClearRequest() {
    closeAct();
    const hasCards = week?.slots.some((s) => s.cards.length > 0) ?? false;
    if (!hasCards) {
      showToast("La setmana ja és buida");
      return;
    }
    setClearConfirmOpen(true);
  }

  async function doClear() {
    setClearConfirmOpen(false);
    try {
      await api.del(`/weeks/${monday}/cards`);
      await refreshWeek();
      showToast("Setmana buidada");
    } catch (e: any) {
      showToast(e.message ?? "Error en buidar la setmana");
    }
  }

  useEffect(() => {
    if (actView === "rec") {
      setSavedWeeks(null);
      api.get<SavedWeekItem[]>("/saved-weeks").then(setSavedWeeks);
    }
  }, [actView]);

  useEffect(() => {
    const onR = () => setMobile(isDayView());
    window.addEventListener("resize", onR);
    window.addEventListener("orientationchange", onR);
    return () => {
      window.removeEventListener("resize", onR);
      window.removeEventListener("orientationchange", onR);
    };
  }, []);

  // Ratolí (drag a partir de 4px) i tàctil amb long-press (~200ms), perquè
  // un toc curt obri el menú i l'scroll no dispari un drag accidental.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  useEffect(() => {
    api.get<AppUser[]>("/users").then(setUsers);
    api.get<RecipeListItem[]>("/recipes?subrecipe=false").then(setRecipes);
  }, []);

  useEffect(() => {
    storeMonday(monday, selectedDay);
    api.get<WeekDto>(`/weeks/${monday}`).then(setWeek);
  }, [monday, selectedDay]);

  // Refresca dades quan l'assistent IA modifica la base de dades.
  useEffect(() => {
    function onChanged() {
      api.get<WeekDto>(`/weeks/${monday}`).then(setWeek);
      api.get<RecipeListItem[]>("/recipes?subrecipe=false").then(setRecipes);
    }
    window.addEventListener("cookmenu:data-changed", onChanged);
    return () => window.removeEventListener("cookmenu:data-changed", onChanged);
  }, [monday]);

  const slotByDayMeal = useMemo(() => {
    const map = new Map<string, WeekSlotDto>();
    week?.slots.forEach((s) => map.set(`${s.day}-${s.mealSlot}`, s));
    return map;
  }, [week]);

  async function refreshWeek() {
    const w = await api.get<WeekDto>(`/weeks/${monday}`);
    setWeek(w);
  }

  // Afegeix una recepta a un slot amb els usuaris actius per defecte d'aquella franja.
  async function addRecipeToSlot(slot: WeekSlotDto, recipeId: number) {
    const defaultUserIds = users
      .filter((u) =>
        isUserActiveOnSlot(u.slotDefaults ?? [], u.slotExceptions ?? [], slot.day, slot.mealSlot)
      )
      .map((u) => u.id);
    await api.post(`/weeks/${monday}/slots/${slot.id}/cards`, {
      recipeId,
      userIds: defaultUserIds,
    });
    await refreshWeek();
  }

  function handleDragStart(e: DragStartEvent) {
    draggingRef.current = true;
    setMenu(null);
    setActiveDrag((e.active.data.current as DragData) ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    const data = activeDrag;
    setActiveDrag(null);
    if (!e.over || !data) return;
    const overId = String(e.over.id);

    // Paperera: treu la targeta del menú.
    if (overId === "trash") {
      await deleteCard(data.cardId);
      return;
    }

    if (!overId.startsWith("slot-")) return;
    const slotId = Number(overId.slice("slot-".length));
    const targetSlot = week?.slots.find((s) => s.id === slotId);
    if (!targetSlot) return;
    if (!isSlotCompatible(targetSlot.mealSlot, data.mealType)) return;
    await api.patch(`/slot-cards/${data.cardId}`, { slotId });
    await refreshWeek();
  }

  async function toggleUser(card: SlotCard, userId: number) {
    const current = card.users.map((u) => u.userId);
    const next = current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId];
    await api.patch(`/slot-cards/${card.id}`, { userIds: next });
    await refreshWeek();
  }

  async function deleteCard(cardId: number) {
    await api.del(`/slot-cards/${cardId}`);
    await refreshWeek();
  }

  // Navegació entre dies a mòbil; en passar del límit, salta de setmana.
  function changeDay(delta: number) {
    setSelectedDay((curr) => {
      const next = curr + delta;
      if (next < 1) {
        setMonday((m) => addDays(m, -7));
        return 7;
      }
      if (next > 7) {
        setMonday((m) => addDays(m, 7));
        return 1;
      }
      return next;
    });
  }

  // Swipe horitzontal per canviar de dia (mòbil).
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const draggingRef = useRef(false);
  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(e: React.TouchEvent) {
    const start = touchStart.current;
    touchStart.current = null;
    if (draggingRef.current) {
      draggingRef.current = false;
      return;
    }
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      changeDay(dx < 0 ? 1 : -1);
    }
  }

  function openCardMenu(card: SlotCard, rect: DOMRect) {
    setMenu({
      cardId: card.id,
      recipeId: card.recipeId,
      baseServings: card.recipe.baseServings,
      userCount: card.users.length,
      x: rect.left,
      y: rect.bottom + 4,
    });
  }

  const todayIso = isoMondayString();
  const isCurrentWeek = todayIso === monday;
  const todayWd = todayWeekday();
  const range = formatWeekRange(monday);

  const sel = useMemo(() => {
    const d = new Date(addDays(monday, selectedDay - 1) + "T00:00:00Z");
    return { dayNum: d.getUTCDate(), month: MONTHS_CA[d.getUTCMonth()] };
  }, [monday, selectedDay]);

  const dayNums = useMemo(
    () => DAYS.map((_, i) => new Date(addDays(monday, i) + "T00:00:00Z").getUTCDate()),
    [monday]
  );

  const daysWithMeals = useMemo(
    () => new Set(week?.slots.filter((s) => s.cards.length > 0).map((s) => s.day) ?? []),
    [week]
  );

  const dayNutrition = useMemo(() => {
    if (!week) return Array(7).fill(null) as (null)[];
    return DAYS.map((_, di) => {
      const day = di + 1;
      const acc = { calories: 0, protein: 0, carbs: 0, fat: 0 };
      let hasData = false;
      for (const slot of week.slots) {
        if (slot.day !== day) continue;
        for (const card of slot.cards) {
          const n = card.nutritionPerServing;
          if (!n) continue;
          acc.calories += n.calories;
          acc.protein += n.protein;
          acc.carbs += n.carbs;
          acc.fat += n.fat;
          hasData = true;
        }
      }
      return hasData ? acc : null;
    });
  }, [week]);

  const handlers = { flashIds, onCardClick: openCardMenu, onToggleUser: toggleUser, onAdd: setPickerSlot };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="week-screen">
        <div className="page-head">
          <div>
            <h1>Setmana</h1>
            <div className="sub">
              {mobile ? `${range.label} · ${range.year}` : `${range.label} · ${range.year} · arrossega per moure`}
            </div>
          </div>
          {mobile && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setMonday(isoMondayString()); setSelectedDay(todayWeekday()); }}
                className="wk-navbtn wk-navbtn-text"
              >
                Avui
              </button>
              <div className="wk-kebab-wrap">
                <button
                  type="button"
                  className={`wk-kebab ${actOpen ? "on" : ""}`}
                  aria-label="Més opcions"
                  onClick={() => setActOpen((o) => !o)}
                >
                  <KebabIcon />
                </button>
                {actOpen && (
                  <WkActionsMenu
                    actView={actView}
                    onClose={closeAct}
                    onSetView={setActView}
                    onPrint={printWeek}
                    onSave={openSaveDialog}
                    onClear={handleClearRequest}
                    onCompra={openCompra}
                    onFill={fillGaps}
                    onReview={openReview}
                    savedWeeks={savedWeeks}
                    onRestore={handleRestoreRequest}
                  />
                )}
              </div>
            </div>
          )}
          {!mobile && (
            <div className="flex gap-2">
              <button
                type="button"
                aria-label="Setmana anterior"
                onClick={() => setMonday(addDays(monday, -7))}
                className="wk-navbtn"
              >
                <ChevronIcon dir="left" />
              </button>
              <button
                type="button"
                onClick={() => { setMonday(isoMondayString()); setSelectedDay(todayWeekday()); }}
                className="wk-navbtn wk-navbtn-text"
              >
                Avui
              </button>
              <button
                type="button"
                aria-label="Setmana següent"
                onClick={() => setMonday(addDays(monday, 7))}
                className="wk-navbtn"
              >
                <ChevronIcon dir="right" />
              </button>
              <div className="wk-kebab-wrap">
                <button
                  type="button"
                  className={`wk-kebab ${actOpen ? "on" : ""}`}
                  aria-label="Més opcions"
                  onClick={() => setActOpen((o) => !o)}
                >
                  <KebabIcon />
                </button>
                {actOpen && (
                  <WkActionsMenu
                    actView={actView}
                    onClose={closeAct}
                    onSetView={setActView}
                    onPrint={printWeek}
                    onSave={openSaveDialog}
                    onClear={handleClearRequest}
                    onCompra={openCompra}
                    onFill={fillGaps}
                    onReview={openReview}
                    savedWeeks={savedWeeks}
                    onRestore={handleRestoreRequest}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {mobile ? (
          <div>
            <div className="wk-mobnav">
              <button className="wk-mobarrow" onClick={() => changeDay(-1)} aria-label="Dia anterior">
                <ChevronIcon dir="left" />
              </button>
              <div className="wk-mobtitle">
                <div className="wk-mobday">{DAY_LABELS_FULL[selectedDay]}</div>
                <div className="text-[13px] font-bold text-muted">
                  {sel.dayNum} de {sel.month}
                  {isCurrentWeek && selectedDay === todayWd ? " · avui" : ""}
                </div>
              </div>
              <button className="wk-mobarrow" onClick={() => changeDay(1)} aria-label="Dia següent">
                <ChevronIcon dir="right" />
              </button>
            </div>
            <div className="wk-mobdots">
              {DAYS.map((d, i) => {
                const day = i + 1;
                const isToday = isCurrentWeek && day === todayWd;
                return (
                  <button
                    key={d}
                    className={`wk-mobdot ${day === selectedDay ? "on" : ""} ${isToday ? "today" : ""}`}
                    onClick={() => setSelectedDay(day)}
                  >
                    <span className="wk-mobdot-d">{d}</span>
                    <span className="wk-mobdot-n">{dayNums[i]}</span>
                    <i className={`wk-mobdot-has ${daysWithMeals.has(day) ? "" : "none"}`} />
                  </button>
                );
              })}
            </div>
            <div className="wk-dayview" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              {SLOT_ROWS.map((row) => {
                const slot = slotByDayMeal.get(`${selectedDay}-${row.slot}`);
                return (
                  <div key={row.slot} className="wk-meal">
                    <div className="wk-meal-hd">
                      <span className={`wk-meal-dot mc-${SLOT_TINT[row.slot]}`} />
                      {row.label}
                    </div>
                    {slot ? (
                      <WeekCell slot={slot} big users={users} activeDrag={activeDrag} handlers={handlers} />
                    ) : (
                      <div className="wk-cell wk-cell-lg" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="wk-scroll">
            <div className="wk-grid">
              {isCurrentWeek && (
                <div
                  className="wk-todaycol"
                  style={{ gridColumn: `${todayWd + 1} / span 1`, gridRow: `1 / ${SLOT_ROWS.length + 3}` }}
                />
              )}
              <div />
              {DAYS.map((d, i) => {
                const isToday = isCurrentWeek && i + 1 === todayWd;
                return (
                  <div key={d} className={`wk-day ${isToday ? "today" : ""}`}>
                    <span>{d}</span>
                    <span className="wk-day-num">{dayNums[i]}</span>
                  </div>
                );
              })}
              {SLOT_ROWS.map((row) => (
                <Fragment key={row.slot}>
                  <div className={`wk-rowlabel mc-${SLOT_TINT[row.slot]}`}>
                    <span className="wk-meal-dot" />
                    {row.label}
                  </div>
                  {DAYS.map((_, di) => {
                    const slot = slotByDayMeal.get(`${di + 1}-${row.slot}`);
                    const isToday = isCurrentWeek && di + 1 === todayWd;
                    return slot ? (
                      <WeekCell
                        key={slot.id}
                        slot={slot}
                        today={isToday}
                        users={users}
                        activeDrag={activeDrag}
                        handlers={handlers}
                      />
                    ) : (
                      <div key={di} className={`wk-cell ${isToday ? "today" : ""}`} />
                    );
                  })}
                </Fragment>
              ))}
              {/* Fila de totals nutricionals — només PC (>880px) */}
              <div className="wk-nutrow-lbl">Total</div>
              {dayNutrition.map((nut, di) => {
                const isToday = isCurrentWeek && di + 1 === todayWd;
                return (
                  <div key={di} className={`wk-nutrow ${isToday ? "today" : ""}`}>
                    {nut ? (
                      <>
                        <span className="wk-nut-kcal">
                          {Math.round(nut.calories)} <small>kcal</small>
                        </span>
                        <div className="wk-nut-bar" aria-hidden>
                          <i className="p" style={{ flexGrow: nut.protein * 4 }} />
                          <i className="c" style={{ flexGrow: nut.carbs * 4 }} />
                          <i className="f" style={{ flexGrow: nut.fat * 9 }} />
                        </div>
                        <span className="wk-nut-macros">
                          <span title="Proteïna"><b className="p" />{Math.round(nut.protein)}g</span>
                          <span title="Hidrats de carboni"><b className="c" />{Math.round(nut.carbs)}g</span>
                          <span title="Greixos"><b className="f" />{Math.round(nut.fat)}g</span>
                        </span>
                      </>
                    ) : (
                      <span className="wk-nut-empty">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Menú contextual de la targeta */}
        {menu && (
          <>
            <div className="wk-pop-scrim" onClick={() => setMenu(null)} />
            <div
              className="wk-menu"
              style={{ left: Math.min(menu.x, window.innerWidth - 184), top: menu.y }}
              role="menu"
            >
              <button
                onClick={() => {
                  setMenu(null);
                  navigate(`/receptes/${menu.recipeId}`);
                }}
              >
                📖 Veure recepta
              </button>
              <button
                onClick={() => {
                  setMenu(null);
                  navigate(`/receptes/${menu.recipeId}/edita`);
                }}
              >
                ✏️ Edita recepta
              </button>
              <button
                onClick={() => {
                  const m = menu;
                  setMenu(null);
                  setModal({ recipeId: m.recipeId, servings: m.userCount || m.baseServings });
                }}
              >
                👨‍🍳 Cuina ara
              </button>
              <div className="wk-menu-sep" />
              <button
                className="danger"
                onClick={() => {
                  const m = menu;
                  setMenu(null);
                  deleteCard(m.cardId);
                }}
              >
                Treu del slot
              </button>
            </div>
          </>
        )}

        {/* Zona de paperera — apareix mentre s'arrossega */}
        {activeDrag && <TrashZone />}

        {toast && (
          <div className="wk-toast">
            {toast}
            {toastAction && (
              <button
                type="button"
                className="wk-toast-act"
                onClick={() => {
                  const a = toastAction;
                  setToast(null);
                  setToastAction(null);
                  a.run();
                }}
              >
                {toastAction.label}
              </button>
            )}
          </div>
        )}

        {reviewOpen && week && (
          <WeekReviewModal
            week={week}
            users={users}
            review={review}
            monday={monday}
            isAdmin={me?.role === "admin"}
            onFill={fillGaps}
            onCompra={() => {
              setReviewOpen(false);
              openCompra();
            }}
            onClose={() => setReviewOpen(false)}
          />
        )}

        {shopOpen && (
          <CompraSetmanaModal
            items={shopItems}
            mealCount={shopMealCount}
            loading={shopLoading}
            removed={shopRemoved}
            onRemove={removeShopItem}
            onAddAll={addAllToShoppingList}
            onClose={() => setShopOpen(false)}
          />
        )}

        <WeekStyles />
      </div>

      <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
        {activeDrag && (
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-card-lg">
            <DishIcon />
          </div>
        )}
      </DragOverlay>

      {modal && (
        <RecipeModal
          recipeId={modal.recipeId}
          servings={modal.servings}
          startCooking={true}
          onClose={() => setModal(null)}
        />
      )}

      {pickerSlot && (
        <RecipePickerSheet
          slot={pickerSlot}
          recipes={recipes}
          onPick={async (recipeId) => {
            await addRecipeToSlot(pickerSlot, recipeId);
            setPickerSlot(null);
          }}
          onClose={() => setPickerSlot(null)}
        />
      )}

      {saveDialogOpen && (
        <SaveWeekDialog
          defaultName={`${range.label} - ${range.year}`}
          onCancel={() => setSaveDialogOpen(false)}
          onConfirm={saveWeek}
        />
      )}

      {pendingRestore && (
        <ConfirmDialog
          title="Substituir la setmana?"
          message={`La setmana actual té receptes assignades. Si continues, es substituiran per les de "${pendingRestore.name}".`}
          confirmLabel="Substitueix"
          onCancel={() => setPendingRestore(null)}
          onConfirm={() => doRestore(pendingRestore)}
        />
      )}

      {clearConfirmOpen && (
        <ConfirmDialog
          title="Buidar la setmana?"
          message="S'eliminaran totes les receptes assignades a la setmana actual. Aquesta acció no es pot desfer."
          confirmLabel="Buida"
          dangerConfirm
          onCancel={() => setClearConfirmOpen(false)}
          onConfirm={doClear}
        />
      )}
    </DndContext>
  );
}

interface CellHandlers {
  flashIds: Set<number>;
  onCardClick: (card: SlotCard, rect: DOMRect) => void;
  onToggleUser: (card: SlotCard, userId: number) => void;
  onAdd: (slot: WeekSlotDto) => void;
}

function WeekCell({
  slot,
  big = false,
  today = false,
  users,
  activeDrag,
  handlers,
}: {
  slot: WeekSlotDto;
  big?: boolean;
  today?: boolean;
  users: AppUser[];
  activeDrag: DragData | null;
  handlers: CellHandlers;
}) {
  const compatible = activeDrag ? isSlotCompatible(slot.mealSlot, activeDrag.mealType) : true;
  const { setNodeRef, isOver } = useDroppable({ id: `slot-${slot.id}`, disabled: !compatible });

  return (
    <div
      ref={setNodeRef}
      className={`wk-cell ${big ? "wk-cell-lg" : ""} ${today ? "today" : ""} ${
        slot.cards.length > 0 ? "has-cards" : ""
      } ${isOver && compatible ? "over" : ""} ${
        activeDrag && !compatible ? "incompatible" : ""
      }`}
    >
      {slot.cards.map((card) => (
        <WeekCard
          key={card.id}
          card={card}
          tint={SLOT_TINT[slot.mealSlot]}
          users={users}
          handlers={handlers}
        />
      ))}
      <button
        type="button"
        className="wk-add"
        aria-label="Afegeix recepta"
        onClick={() => handlers.onAdd(slot)}
      >
        <PlusIcon />
      </button>
    </div>
  );
}

function WeekCard({
  card,
  tint,
  users,
  handlers,
}: {
  card: SlotCard;
  tint: string;
  users: AppUser[];
  handlers: CellHandlers;
}) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: {
      recipeId: card.recipeId,
      mealType: card.recipe.mealType,
      cardId: card.id,
    } satisfies DragData,
  });
  const selected = card.users.map((u) => u.userId);

  return (
    <div
      ref={setNodeRef}
      className={`wk-card mc-${tint} ${isDragging ? "dragging" : ""} ${
        handlers.flashIds.has(card.id) ? "flash" : ""
      }`}
      {...attributes}
      {...listeners}
      onClick={(e) => handlers.onCardClick(card, e.currentTarget.getBoundingClientRect())}
    >
      <span className="wk-name">{card.recipe.name}</span>
      <div className="wk-people" onPointerDown={(e) => e.stopPropagation()}>
        {users.map((u) => {
          const on = selected.includes(u.id);
          return (
            <button
              key={u.id}
              type="button"
              className={`wk-person ${on ? "on" : "off"}`}
              title={u.name}
              draggable={false}
              onClick={(e) => {
                e.stopPropagation();
                handlers.onToggleUser(card, u.id);
              }}
            >
              {u.avatarEmoji}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TrashZone() {
  const { setNodeRef, isOver } = useDroppable({ id: "trash" });
  return (
    <div ref={setNodeRef} className={`wk-trash ${isOver ? "over" : ""}`}>
      <TrashIcon />
      <span>{isOver ? "Deixa anar per treure" : "Arrossega aquí per treure"}</span>
    </div>
  );
}

function WkActionsMenu({
  actView,
  onClose,
  onSetView,
  onPrint,
  onSave,
  onClear,
  onCompra,
  onFill,
  onReview,
  savedWeeks,
  onRestore,
}: {
  actView: "main" | "rec";
  onClose: () => void;
  onSetView: (v: "main" | "rec") => void;
  onPrint: () => void;
  onSave: () => void;
  onClear: () => void;
  onCompra: () => void;
  onFill: () => void;
  onReview: () => void;
  savedWeeks: SavedWeekItem[] | null;
  onRestore: (sw: SavedWeekItem) => void;
}) {
  return (
    <>
      <div className="wk-act-scrim" onClick={onClose} />
      <div className="wk-actions-pop">
        {actView === "main" ? (
          <>
            <button type="button" className="wk-ami" onClick={onSave}>
              <WkSaveIcon /> Desa la setmana
            </button>
            <button type="button" className="wk-ami" onClick={() => onSetView("rec")}>
              <WkHistoryIcon /> Recupera una setmana
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "auto", color: "var(--faint)" }} aria-hidden><polyline points="9 18 15 12 9 6" /></svg>
            </button>
            <button type="button" className="wk-ami" onClick={onFill}>
              <WkSparkleIcon /> Omple buits
            </button>
            <button type="button" className="wk-ami" onClick={onReview}>
              <WkReviewIcon /> Revisa la setmana
            </button>
            <button type="button" className="wk-ami" onClick={onPrint}>
              <WkPrintIcon /> Imprimeix la setmana
            </button>
            <button type="button" className="wk-ami wk-ami-danger" onClick={onClear}>
              <WkClearIcon /> Esborra la setmana
            </button>
            <div className="wk-menu-sep" />
            <button type="button" className="wk-ami wk-ami-primary" onClick={onCompra}>
              <WkCartIcon /> Compra
            </button>
          </>
        ) : (
          <>
            <button type="button" className="wk-ami wk-ami-back" onClick={() => onSetView("main")}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="15 18 9 12 15 6" /></svg>
              Setmanes desades
            </button>
            <div className="wk-menu-sep" />
            {savedWeeks === null ? (
              <div style={{ padding: "8px 11px", fontSize: 13, color: "var(--muted)" }}>Carregant…</div>
            ) : savedWeeks.length === 0 ? (
              <div style={{ padding: "8px 11px", fontWeight: 600, fontSize: 13, color: "var(--muted)" }}>
                Encara no n'hi ha cap de desada.
              </div>
            ) : (
              savedWeeks.map((sw) => (
                <button
                  key={sw.id}
                  type="button"
                  className="wk-ami"
                  onClick={() => onRestore(sw)}
                >
                  {sw.name}
                </button>
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}

function KebabIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="5" r="1.9" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="12" cy="19" r="1.9" />
    </svg>
  );
}
function WkSaveIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8"/><path d="M7 3v5h7"/></svg>;
}
function WkHistoryIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 3v6h6"/><path d="M3.5 9a9 9 0 1 0 2-3.3L3 9"/><path d="M12 7v5l4 2"/></svg>;
}
function WkSparkleIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z"/></svg>;
}
function WkReviewIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M16 4h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2.5" width="8" height="4" rx="1.2"/><path d="M9 13l2 2 4-4"/></svg>;
}
function WkPrintIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9V3h12v6"/><path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="7" rx="1"/></svg>;
}
function WkClearIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M6 6l1 14h10l1-14"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
}
function WkCartIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2 3h3l2.5 13a1 1 0 0 0 1 .8h9.2a1 1 0 0 0 1-.8L21 7H6"/></svg>;
}

function ChevronIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {dir === "left" ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M6 6l1 14h10l1-14" />
    </svg>
  );
}

function DishIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M7 3v7" />
      <path d="M5 3v3a2 2 0 0 0 4 0V3" />
      <path d="M7 10v11" />
      <path d="M17 3c-1.5 0-2.5 2-2.5 4.5S15.5 12 17 12v9" />
    </svg>
  );
}


function ConfirmDialog({
  title,
  message,
  confirmLabel,
  dangerConfirm,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  dangerConfirm?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [loading, setLoading] = useState(false);

  async function handle() {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  }

  return (
    <>
      <div className="wk-dialog-scrim" onClick={onCancel} />
      <div className="wk-dialog" role="dialog" aria-modal="true">
        <h2 className="wk-dialog-title">{title}</h2>
        <p className="wk-dialog-msg">{message}</p>
        <div className="wk-dialog-actions">
          <button type="button" className="wk-dialog-btn-cancel" onClick={onCancel} disabled={loading}>
            Cancel·la
          </button>
          <button
            type="button"
            className={dangerConfirm ? "wk-dialog-btn-danger" : "wk-dialog-btn-ok"}
            onClick={handle}
            disabled={loading}
          >
            {loading ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

function SaveWeekDialog({
  defaultName,
  onCancel,
  onConfirm,
}: {
  defaultName: string;
  onCancel: () => void;
  onConfirm: (name: string) => Promise<string | null>;
}) {
  const [name, setName] = useState(defaultName);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    const err = await onConfirm(trimmed);
    if (err) {
      setError(err);
      setLoading(false);
    }
  }

  return (
    <>
      <div className="wk-dialog-scrim" onClick={onCancel} />
      <div className="wk-dialog" role="dialog" aria-modal="true">
        <h2 className="wk-dialog-title">Desa la setmana</h2>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            className="wk-dialog-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            placeholder="Nom de la setmana"
            disabled={loading}
          />
          {error && <div className="wk-dialog-error">{error}</div>}
          <div className="wk-dialog-actions">
            <button type="button" className="wk-dialog-btn-cancel" onClick={onCancel} disabled={loading}>
              Cancel·la
            </button>
            <button type="submit" className="wk-dialog-btn-ok" disabled={!name.trim() || loading}>
              {loading ? "Desant…" : "Desa"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

const NUT_STATUS_LABEL: Record<NutritionStatus, string> = {
  ok: "dins del rang",
  warn: "una mica fora del rang",
  bad: "fora del rang",
  na: "sense prou dades",
};

function NutMetric({ label, value, hint, status }: { label: string; value: string; hint: string; status: NutritionStatus }) {
  return (
    <div className="wk-nutp-m" title={NUT_STATUS_LABEL[status]}>
      <span className={`wk-nutp-dot ${status}`} aria-label={NUT_STATUS_LABEL[status]} />
      <div className="wk-nutp-mt">
        <span className="wk-nutp-ml">{label}</span>
        <b>{value}</b>
        <span className="wk-nutp-mh">{hint}</span>
      </div>
    </div>
  );
}

function NutPerson({ p }: { p: PersonNutrition }) {
  const energyHint = p.targetKcal == null
    ? p.missingProfile.length
      ? `falta ${p.missingProfile.join(", ")} al perfil`
      : "sense objectiu"
    : p.avgKcal == null
      ? `objectiu ~${p.targetKcal} · cap dia complet`
      : `${Math.round((p.avgKcal / p.targetKcal) * 100)}% de ~${p.targetKcal}`;
  const range = (r: [number, number]) => `${r[0]}–${r[1]}%`;
  const notes = [
    p.completeDays > 0 && p.completeDays < 7 ? `energia de ${p.completeDays} dia${p.completeDays > 1 ? "s" : ""} complet${p.completeDays > 1 ? "s" : ""}` : null,
    p.cardsWithoutData > 0 ? `${p.cardsWithoutData} plat${p.cardsWithoutData > 1 ? "s" : ""} sense dades nutricionals` : null,
    ...p.targetNotes,
  ].filter(Boolean);

  return (
    <div className="wk-nutp">
      <div className="wk-nutp-hd">
        <span className="wk-nutp-av" style={{ background: p.avatarColor }} aria-hidden>
          {p.avatarEmoji}
        </span>
        <b>{p.name}</b>
        {p.age != null && <span className="wk-nutp-age">{p.age} anys</span>}
      </div>
      {p.plannedMeals === 0 ? (
        <div className="wk-rev-note">Cap plat assignat aquesta setmana</div>
      ) : (
        <div className="wk-nutp-grid">
          <NutMetric label="Energia" value={p.avgKcal != null ? `${p.avgKcal} kcal` : "—"} hint={energyHint} status={p.status.energy} />
          <NutMetric label="Proteïna" value={p.macros ? `${p.macros.protein}%` : "—"} hint={range(p.ranges.protein)} status={p.status.protein} />
          <NutMetric label="Hidrats" value={p.macros ? `${p.macros.carbs}%` : "—"} hint={range(p.ranges.carbs)} status={p.status.carbs} />
          <NutMetric label="Greixos" value={p.macros ? `${p.macros.fat}%` : "—"} hint={range(p.ranges.fat)} status={p.status.fat} />
          <NutMetric label="Ultraprocessats" value={p.ultraPct != null ? `${p.ultraPct}%` : "—"} hint="de l'energia · ≤10%" status={p.status.ultra} />
        </div>
      )}
      {notes.length > 0 && <div className="wk-nutp-notes">{notes.join(" · ")}</div>}
    </div>
  );
}

/** Revisió nutricional per persona dins de «Revisa la setmana», amb comentari opcional de la IA. */
function WeekNutritionCard({ people, monday, isAdmin }: { people: PersonNutrition[] | null; monday: string; isAdmin: boolean }) {
  const [ai, setAi] = useState<{ loading: boolean; text?: string; error?: string }>({ loading: false });

  async function askAi() {
    setAi({ loading: true });
    try {
      const res = await api.post<{ comment: string }>(`/weeks/${monday}/review/ai`);
      setAi({ loading: false, text: res.comment });
    } catch (e: any) {
      setAi({ loading: false, error: e.message ?? "No s'ha pogut obtenir el comentari" });
    }
  }

  return (
    <div className="wk-rev-card wk-rev-wide">
      <div className="wk-rev-t">Nutrició per persona</div>
      {people === null ? (
        <div className="wk-rev-note">Calculant…</div>
      ) : people.length === 0 ? (
        <div className="wk-rev-note">Ningú no menja aquesta setmana</div>
      ) : (
        <div className="wk-nutp-list">
          {people.map((p) => (
            <NutPerson key={p.userId} p={p} />
          ))}
        </div>
      )}

      {isAdmin && people !== null && people.length > 0 && (
        <div className="wk-nutp-ai">
          {ai.text ? (
            <div className="wk-nutp-ai-text">{ai.text}</div>
          ) : (
            <button type="button" className="wk-rev-chip wk-nutp-ai-btn" disabled={ai.loading} onClick={askAi}>
              <WkSparkleIcon /> {ai.loading ? "Demanant a la IA…" : "Comentari de la IA"}
            </button>
          )}
          {ai.error && <div className="wk-rev-note">{ai.error}</div>}
        </div>
      )}

      <div className="wk-nutp-fine">
        Orientatiu: rangs EFSA, una ració per plat assignat i energia només dels dies amb tots els àpats planificats.
      </div>
    </div>
  );
}

interface WeekReviewData {
  weekRepeats: { recipeId: number; count: number }[];
  recentRepeatRecipeIds: number[];
  nutrition?: PersonNutrition[];
}

function WeekReviewModal({
  week,
  users,
  review,
  monday,
  isAdmin,
  onFill,
  onCompra,
  onClose,
}: {
  week: WeekDto;
  users: AppUser[];
  review: WeekReviewData | null;
  monday: string;
  isAdmin: boolean;
  onFill: () => void;
  onCompra: () => void;
  onClose: () => void;
}) {
  const rv = useMemo(() => {
    const rowSlots = new Set(SLOT_ROWS.map((r) => r.slot));
    const labelOf = (s: WeekSlotDto) =>
      `${SLOT_ROWS.find((r) => r.slot === s.mealSlot)?.label} · ${DAY_LABELS_FULL[s.day]}`;
    const activeUsers = (s: WeekSlotDto) =>
      users.filter((u) => isUserActiveOnSlot(u.slotDefaults ?? [], u.slotExceptions ?? [], s.day, s.mealSlot));

    const slots = week.slots
      .filter((s) => rowSlots.has(s.mealSlot))
      .sort((a, b) => a.day - b.day || SLOT_ROWS.findIndex((r) => r.slot === a.mealSlot) - SLOT_ROWS.findIndex((r) => r.slot === b.mealSlot));

    // Franges "esperades": on hi ha almenys un usuari que hi menja.
    const expected = slots.filter((s) => activeUsers(s).length > 0);
    const empties = expected.filter((s) => s.cards.length === 0).map(labelOf);
    const covered = expected.length - empties.length;

    const cards = slots.flatMap((s) => s.cards);
    const names = new Map(cards.map((c) => [c.recipeId, c.recipe.name]));

    // Comensals que hi haurien de menjar però no surten a cap plat de la franja.
    const missing = slots
      .filter((s) => s.cards.length > 0)
      .map((s) => {
        const assigned = new Set(s.cards.flatMap((c) => c.users.map((u) => u.userId)));
        return { label: labelOf(s), who: activeUsers(s).filter((u) => !assigned.has(u.id)) };
      })
      .filter((m) => m.who.length > 0);
    const nobody = cards.filter((c) => c.users.length === 0).length;

    const perMeal = SLOT_ROWS.map((row) => ({
      ...row,
      n: slots.filter((s) => s.mealSlot === row.slot && s.cards.length > 0).length,
    }));

    return {
      planned: cards.length,
      unique: names.size,
      expected: expected.length,
      covered,
      empties,
      missing,
      nobody,
      perMeal,
      names,
    };
  }, [week, users]);

  const cover = rv.expected ? Math.round((rv.covered / rv.expected) * 100) : 100;
  const warnings = rv.missing.length + (rv.nobody > 0 ? 1 : 0);

  return (
    <div className="wk-modal-scrim" onClick={onClose}>
      <div className="wk-modal wk-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="wk-modal-hd">
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Revisió de la setmana</div>
            <div className="wk-shop-subtitle">
              {rv.planned} àpats · {rv.unique} receptes diferents
            </div>
          </div>
          <button type="button" className="wk-shop-close" aria-label="Tanca" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="wk-rev-body">
          <div className="wk-rev-card">
            <div className="wk-rev-t">Cobertura</div>
            <div className="wk-rev-big">
              {rv.covered}
              <small>/{rv.expected} àpats</small>
            </div>
            <div className="wk-rev-bar">
              <i style={{ width: cover + "%" }} />
            </div>
            <div className="wk-rev-note">
              {rv.empties.length ? `${rv.empties.length} àpats buits` : "Setmana completa 🎉"}
            </div>
          </div>

          <div className="wk-rev-card">
            <div className="wk-rev-t">Per àpat</div>
            <div className="wk-rev-list">
              {rv.perMeal.map((m) => (
                <div key={m.slot} className="wk-rev-row">
                  <span className={`wk-meal-dot mc-${SLOT_TINT[m.slot]}`} />
                  <span className="wk-rev-l">{m.label}</span>
                  <b>{m.n}/7</b>
                </div>
              ))}
            </div>
          </div>

          <div className="wk-rev-card">
            <div className="wk-rev-t">Varietat · dinars i sopars</div>
            {!review ? (
              <div className="wk-rev-note">Comprovant…</div>
            ) : review.weekRepeats.length === 0 ? (
              <div className="wk-rev-note">Cap plat repetit — molt variat!</div>
            ) : (
              <div className="wk-rev-list">
                {review.weekRepeats.slice(0, 4).map((r) => (
                  <div key={r.recipeId} className="wk-rev-row">
                    <span className="wk-rev-l">{rv.names.get(r.recipeId)}</span>
                    <b>×{r.count}</b>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="wk-rev-card">
            <div className="wk-rev-t">Ja menjat fa poc</div>
            {!review ? (
              <div className="wk-rev-note">Comprovant…</div>
            ) : review.recentRepeatRecipeIds.length === 0 ? (
              <div className="wk-rev-note">Res repetit de les 2 últimes setmanes</div>
            ) : (
              <div className="wk-rev-chips">
                {review.recentRepeatRecipeIds.map((id) => (
                  <span key={id} className="wk-rev-chip">
                    {rv.names.get(id)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <WeekNutritionCard people={review ? review.nutrition ?? [] : null} monday={monday} isAdmin={isAdmin} />

          {warnings > 0 && (
            <div className="wk-rev-card wk-rev-wide wk-rev-warn">
              <div className="wk-rev-t">Comensals</div>
              <div className="wk-rev-list">
                {rv.nobody > 0 && (
                  <div className="wk-rev-row">
                    <span className="wk-rev-l">
                      {rv.nobody} plat{rv.nobody > 1 ? "s" : ""} sense ningú assignat
                    </span>
                  </div>
                )}
                {rv.missing.slice(0, 6).map((m) => (
                  <div key={m.label} className="wk-rev-row">
                    <span className="wk-rev-l">{m.label}</span>
                    <span className="wk-rev-who" title={m.who.map((u) => u.name).join(", ")}>
                      falta {m.who.map((u) => u.avatarEmoji).join(" ")}
                    </span>
                  </div>
                ))}
                {rv.missing.length > 6 && <div className="wk-rev-note">i {rv.missing.length - 6} més</div>}
              </div>
            </div>
          )}

          {rv.empties.length > 0 && (
            <div className="wk-rev-card wk-rev-wide">
              <div className="wk-rev-t">Àpats buits</div>
              <div className="wk-rev-chips">
                {rv.empties.slice(0, 10).map((e) => (
                  <span key={e} className="wk-rev-chip">
                    {e}
                  </span>
                ))}
                {rv.empties.length > 10 && <span className="wk-rev-chip more">+{rv.empties.length - 10}</span>}
              </div>
            </div>
          )}
        </div>

        <div className="wk-rev-foot">
          {rv.empties.length > 0 && (
            <button type="button" className="wk-rev-btn" onClick={onFill}>
              <WkSparkleIcon /> Omple els buits
            </button>
          )}
          <button type="button" className="wk-rev-btn primary" onClick={onCompra}>
            <WkCartIcon /> Fes la compra
          </button>
        </div>
      </div>
    </div>
  );
}

function CompraSetmanaModal({
  items,
  mealCount,
  loading,
  removed,
  onRemove,
  onAddAll,
  onClose,
}: {
  items: WeekShopItem[];
  mealCount: number;
  loading: boolean;
  removed: Record<string, true>;
  onRemove: (name: string) => void;
  onAddAll: () => void;
  onClose: () => void;
}) {
  const visible = items.filter((it) => !removed[it.name]);

  function fmtQty(it: WeekShopItem): string {
    if (it.quantity == null) return it.unit ?? "";
    if (!it.unit) return String(it.quantity);
    return `${it.quantity} ${it.unit}`;
  }

  return (
    <div className="wk-modal-scrim" onClick={onClose}>
      <div className="wk-modal wk-modal-shop" onClick={(e) => e.stopPropagation()}>
        <div className="wk-modal-hd">
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Compra de la setmana</div>
            <div className="wk-shop-subtitle">
              {loading ? "Carregant…" : `${visible.length} ingredients · ${mealCount} àpats planificats`}
            </div>
          </div>
          <button type="button" className="wk-shop-close" aria-label="Tanca" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="wk-modal-list wk-shop-list">
          {loading ? (
            <div className="wk-shop-empty">Carregant ingredients…</div>
          ) : visible.length === 0 ? (
            <div className="wk-shop-empty">
              {items.length > 0
                ? "Has tret tots els articles de la llista."
                : "Cap recepta planificada encara."}
            </div>
          ) : (
            visible.map((it) => (
              <div key={it.name} className="wk-shop-row">
                <span className="wk-shop-qty">{fmtQty(it)}</span>
                <span className="wk-shop-name">{it.name}</span>
                <button
                  type="button"
                  className="wk-shop-del"
                  aria-label={`Treu ${it.name} de la llista`}
                  title="Treu de la llista"
                  onClick={() => onRemove(it.name)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))
          )}
        </div>

        <button
          type="button"
          className="wk-shop-footer-btn"
          disabled={loading || visible.length === 0}
          onClick={onAddAll}
        >
          <WkCartIcon /> Afegeix {visible.length} articles a la llista
        </button>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function WeekStyles() {
  return (
    <style>{`
      .week-screen .wk-scroll{ overflow-x:auto; margin:0 -4px; padding:0 4px 4px; }
      .week-screen .wk-grid{ position:relative; isolation:isolate; display:grid; grid-template-columns:70px repeat(7,minmax(124px,1fr)); gap:8px; min-width:780px; align-items:stretch; }
      .week-screen .wk-todaycol{ position:absolute; inset:0 -3px; z-index:-1; border-radius:15px; background:color-mix(in oklab,var(--accent) 9%, transparent); pointer-events:none; }
      @media(max-width:880px){
        .week-screen .wk-grid{ grid-template-columns:42px repeat(7,minmax(82px,1fr)); gap:6px; min-width:0; }
        .week-screen .wk-rowlabel{ min-height:0; font-size:11px; }
        .week-screen .wk-cell{ min-height:74px; padding:5px; gap:5px; border-radius:11px; }
        .week-screen .wk-name{ font-size:11px; line-height:1.18; }
        .week-screen .wk-person{ font-size:13px; }
        .week-screen .wk-people{ gap:4px; }
        .week-screen .wk-add{ min-height:28px; }
      }
      .week-screen .wk-day{ display:flex; align-items:baseline; justify-content:center; gap:6px; font-weight:800; color:var(--muted); padding:8px 0; border-radius:10px; background:var(--surface-secondary); position:sticky; top:0; }
      .week-screen .wk-day-num{ font-family:var(--font-head); font-weight:600; font-size:16px; line-height:1; color:var(--foreground); }
      .week-screen .wk-day.today{ background:var(--accent); color:#fff; }
      .week-screen .wk-day.today .wk-day-num{ color:#fff; }
      .week-screen .wk-rowlabel{ display:flex; align-items:center; gap:7px; font-weight:800; font-size:13px; color:var(--faint); writing-mode:vertical-rl; transform:rotate(180deg); justify-content:center; min-height:90px; }
      .week-screen .wk-rowlabel .wk-meal-dot{ width:8px; height:8px; flex-basis:8px; }
      .week-screen .wk-cell{ min-height:90px; border:1px solid var(--border); border-radius:13px; background:var(--surface); padding:6px; display:flex; flex-direction:column; gap:6px; transition:.12s; }
      .week-screen .wk-cell.today{ border-color:color-mix(in oklab,var(--accent) 28%, var(--border)); }
      .week-screen .wk-cell.over{ border-color:var(--accent); background:var(--accent-soft); box-shadow:0 0 0 2px var(--accent-soft); }
      .week-screen .wk-cell.incompatible{ opacity:.4; }

      .week-screen .wk-card{ position:relative; border:0; border-left:3px solid var(--mc,var(--border-strong)); background:color-mix(in oklab,var(--mc,var(--border-strong)) 8%, var(--surface)); border-radius:8px; padding:8px 9px; min-height:40px; display:flex; flex-direction:column; gap:6px; text-align:left; cursor:pointer; transition:.12s; }
      .week-screen .wk-card:hover{ background:color-mix(in oklab,var(--mc,var(--border-strong)) 15%, var(--surface)); }
      .week-screen .wk-card.dragging{ opacity:.4; }
      .week-screen .wk-name{ font-weight:700; font-size:12px; line-height:1.22; padding-right:2px; color:var(--foreground); }
      .week-screen .wk-people{ display:flex; gap:5px; margin-top:1px; flex-wrap:wrap; }
      .week-screen .wk-person{ border:0; background:transparent; padding:0; cursor:pointer; font-size:15px; line-height:1; transition:.12s; }
      .week-screen .wk-person.off{ filter:grayscale(1); opacity:.32; }
      .week-screen .wk-person:hover{ transform:translateY(-1px); }
      .week-screen .wk-add{ flex:1 1 auto; display:grid; place-items:center; min-height:34px; border:1.4px dashed var(--border-strong); border-radius:9px; background:transparent; color:var(--faint); cursor:pointer; transition:.12s; }
      .week-screen .wk-add:hover{ border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }
      .week-screen .wk-cell.has-cards .wk-add{ flex:0 0 auto; min-height:26px; border-color:transparent; color:var(--faint); }
      .week-screen .wk-cell.has-cards .wk-add:hover{ border-color:transparent; color:var(--accent); background:var(--accent-soft); }
      @media(hover:hover){
        .week-screen .wk-cell.has-cards .wk-add{ opacity:0; }
        .week-screen .wk-cell.has-cards:hover .wk-add, .week-screen .wk-cell.has-cards .wk-add:focus-visible{ opacity:1; }
      }

      .week-screen .wk-navbtn{ display:inline-flex; align-items:center; justify-content:center; height:38px; min-width:38px; padding:0 12px; border-radius:12px; border:1px solid var(--border-strong); background:var(--surface); color:var(--foreground); font-weight:700; font-size:14px; cursor:pointer; box-shadow:var(--shadow-card-sm); transition:.15s; }
      .week-screen .wk-navbtn:hover{ background:var(--surface-secondary); }
      .week-screen .wk-navbtn:active{ transform:translateY(1px); }
      .week-screen .wk-navbtn-text{ padding:0 16px; }

      .wk-pop-scrim{ position:fixed; inset:0; z-index:60; }
      .wk-menu{ position:fixed; z-index:61; min-width:168px; background:var(--surface); border:1px solid var(--border); border-radius:12px; box-shadow:var(--shadow-card-lg); padding:6px; display:flex; flex-direction:column; }
      .wk-menu button{ display:flex; align-items:center; gap:8px; text-align:left; border:0; background:transparent; cursor:pointer; font:inherit; font-weight:700; font-size:13px; color:var(--foreground); padding:8px 10px; border-radius:8px; }
      .wk-menu button:hover{ background:var(--surface-secondary); }
      .wk-menu button.danger{ color:var(--danger); }
      .wk-menu button.danger:hover{ background:var(--danger-soft); }
      .wk-menu-sep{ height:1px; background:var(--border); margin:4px 6px; }

      .wk-trash{ position:fixed; left:50%; bottom:26px; transform:translateX(-50%); z-index:61; display:flex; align-items:center; gap:9px;
        padding:13px 22px; border-radius:14px; font-weight:800; font-size:14px; color:var(--danger);
        background:var(--surface); border:1.6px dashed color-mix(in oklab,var(--danger) 50%, var(--border)); box-shadow:var(--shadow-card-lg); transition:.12s; }
      .wk-trash.over{ background:var(--danger-soft); border-style:solid; transform:translateX(-50%) scale(1.05); }
      @media(max-width:999.98px){ .wk-trash{ bottom:calc(76px + env(safe-area-inset-bottom)); } }

      /* vista d'un sol dia (mòbil) */
      .week-screen .wk-mobnav{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:12px; }
      .week-screen .wk-mobarrow{ width:44px; height:44px; border-radius:13px; border:1px solid var(--border-strong); background:var(--surface); color:var(--foreground); display:grid; place-items:center; cursor:pointer; box-shadow:var(--shadow-card-sm); }
      .week-screen .wk-mobarrow:active{ background:var(--surface-secondary); transform:translateY(1px); }
      .week-screen .wk-mobtitle{ text-align:center; }
      .week-screen .wk-mobday{ font-family:var(--font-head); font-weight:600; font-size:23px; line-height:1.1; }
      .week-screen .wk-mobdots{ display:flex; gap:6px; margin-bottom:18px; }
      .week-screen .wk-mobdot{ flex:1; display:flex; flex-direction:column; align-items:center; gap:1px; padding:7px 0 6px; border-radius:11px; border:0; background:var(--surface-secondary); color:var(--muted); font-weight:800; font-size:12px; cursor:pointer; transition:.12s; }
      .week-screen .wk-mobdot-n{ font-family:var(--font-head); font-weight:600; font-size:17px; line-height:1.1; color:var(--foreground); }
      .week-screen .wk-mobdot-has{ width:5px; height:5px; border-radius:50%; background:var(--accent); margin-top:2px; }
      .week-screen .wk-mobdot-has.none{ visibility:hidden; }
      .week-screen .wk-mobdot.on{ background:var(--accent); color:#fff; }
      .week-screen .wk-mobdot.on .wk-mobdot-n{ color:#fff; }
      .week-screen .wk-mobdot.on .wk-mobdot-has{ background:#fff; }
      .week-screen .wk-mobdot.today:not(.on){ box-shadow:inset 0 0 0 2px var(--accent-soft); color:var(--accent-soft-foreground); }
      .week-screen .wk-dayview{ display:flex; flex-direction:column; gap:18px; touch-action:pan-y; }
      .week-screen .wk-meal-hd{ display:flex; align-items:center; gap:8px; font-weight:800; font-size:14px; color:var(--muted); margin-bottom:8px; }
      .week-screen .wk-meal-dot{ width:10px; height:10px; border-radius:50%; background:var(--mc,var(--border-strong)); flex:0 0 10px; }
      .week-screen .wk-cell-lg{ min-height:0; }
      .week-screen .wk-cell-lg .wk-card{ padding:13px 14px; border-radius:11px; }
      .week-screen .wk-cell-lg .wk-name{ font-size:15.5px; }
      .week-screen .wk-cell-lg .wk-people{ gap:10px; margin-top:5px; }
      .week-screen .wk-cell-lg .wk-person{ font-size:20px; }
      .week-screen .wk-cell-lg .wk-add{ min-height:48px; }

      /* Fila de totals nutricionals */
      .week-screen .wk-nutrow-lbl{ display:flex; align-items:center; justify-content:center; font-weight:800; font-size:13px; color:var(--faint); writing-mode:vertical-rl; transform:rotate(180deg); }
      .week-screen .wk-nutrow{ display:flex; flex-direction:column; gap:6px; padding:8px 9px; border-radius:11px; background:var(--surface); border:1px solid var(--border); }
      .week-screen .wk-nutrow.today{ border-color:color-mix(in oklab,var(--accent) 28%, var(--border)); }
      .week-screen .wk-nut-kcal{ font-family:var(--font-head); font-weight:600; font-size:17px; line-height:1; color:var(--foreground); white-space:nowrap; }
      .week-screen .wk-nut-kcal small{ font-family:var(--font-body); font-weight:700; font-size:11px; color:var(--muted); }
      .week-screen .wk-nut-bar{ display:flex; gap:2px; height:5px; border-radius:3px; overflow:hidden; }
      .week-screen .wk-nut-bar i{ flex-basis:0; min-width:2px; }
      .week-screen .wk-nut-bar .p, .week-screen .wk-nut-macros b.p{ background:oklch(64% 0.13 20); }
      .week-screen .wk-nut-bar .c, .week-screen .wk-nut-macros b.c{ background:oklch(76% 0.12 80); }
      .week-screen .wk-nut-bar .f, .week-screen .wk-nut-macros b.f{ background:oklch(62% 0.09 150); }
      .week-screen .wk-nut-macros{ display:flex; gap:8px; flex-wrap:wrap; color:var(--muted); font-weight:700; font-size:11px; font-variant-numeric:tabular-nums; }
      .week-screen .wk-nut-macros > span{ display:inline-flex; align-items:center; gap:3px; }
      .week-screen .wk-nut-macros b{ width:6px; height:6px; border-radius:50%; }
      .week-screen .wk-nut-empty{ color:var(--faint); font-size:13px; display:flex; align-items:center; justify-content:center; flex:1; }
      @media(max-width:880px){
        .week-screen .wk-nutrow,
        .week-screen .wk-nutrow-lbl{ display:none; }
      }

      /* kebab menu */
      .week-screen .wk-kebab-wrap{ position:relative; }
      .week-screen .wk-kebab{ width:38px; height:38px; border:0; background:transparent; border-radius:11px; color:var(--foreground); cursor:pointer; display:grid; place-items:center; transition:.12s; }
      .week-screen .wk-kebab:hover, .week-screen .wk-kebab.on{ background:var(--surface-secondary); }
      .wk-act-scrim{ position:fixed; inset:0; z-index:55; }
      .wk-actions-pop{ position:absolute; right:0; top:calc(100% + 6px); z-index:56; width:252px; background:var(--surface); border:1px solid var(--border); border-radius:14px; box-shadow:var(--shadow-card-lg); padding:6px; animation:pop .14s ease; }
      .wk-ami{ display:flex; align-items:center; gap:11px; width:100%; text-align:left; border:0; background:transparent; cursor:pointer; font:inherit; font-weight:700; font-size:14px; color:var(--foreground); padding:10px 11px; border-radius:10px; }
      .wk-ami:hover{ background:var(--surface-secondary); }
      .wk-ami > svg{ color:var(--accent-soft-foreground); flex:none; }
      .wk-ami-primary{ color:var(--accent); }
      .wk-ami-primary > svg{ color:var(--accent); }
      .wk-ami-back{ color:var(--muted); font-weight:800; font-size:11.5px; letter-spacing:.05em; text-transform:uppercase; padding:7px 11px; }
      .wk-ami-back > svg{ color:var(--muted); }
      .wk-ami-danger{ color:var(--danger); }
      .wk-ami-danger > svg{ color:var(--danger); }
      .wk-ami-danger:hover{ background:var(--danger-soft); }

      /* toast */
      .wk-toast{ position:fixed; left:50%; bottom:26px; transform:translateX(-50%); z-index:70; background:var(--foreground); color:var(--background); font-weight:700; font-size:14px; padding:12px 20px; border-radius:13px; box-shadow:var(--shadow-card-lg); animation:pop .16s ease; white-space:nowrap; }
      @media(max-width:999.98px){ .wk-toast{ bottom:calc(76px + env(safe-area-inset-bottom)); } }

      /* diàlegs */
      .wk-dialog-scrim{ position:fixed; inset:0; z-index:72; background:rgba(0,0,0,.4); }
      .wk-dialog{ position:fixed; top:50%; left:50%; transform:translate(-50%,-50%); z-index:73; background:var(--surface); border:1px solid var(--border); border-radius:16px; box-shadow:var(--shadow-card-lg); padding:24px; width:min(420px,calc(100vw - 32px)); animation:pop .16s ease; }
      .wk-dialog-title{ font-family:var(--font-head); font-size:18px; font-weight:700; margin:0 0 16px; }
      .wk-dialog-input{ width:100%; border:1.5px solid var(--border-strong); border-radius:10px; padding:10px 13px; font:inherit; font-size:15px; background:var(--surface); color:var(--foreground); box-sizing:border-box; }
      .wk-dialog-input:focus{ outline:none; border-color:var(--accent); }
      .wk-dialog-error{ color:var(--danger); font-size:13px; font-weight:600; margin-top:8px; }
      .wk-dialog-actions{ display:flex; justify-content:flex-end; gap:10px; margin-top:20px; }
      .wk-dialog-btn-cancel{ border:1px solid var(--border-strong); background:transparent; border-radius:10px; padding:9px 16px; font:inherit; font-size:14px; font-weight:700; cursor:pointer; color:var(--foreground); }
      .wk-dialog-btn-cancel:hover{ background:var(--surface-secondary); }
      .wk-dialog-btn-ok{ border:0; background:var(--accent); color:#fff; border-radius:10px; padding:9px 20px; font:inherit; font-size:14px; font-weight:700; cursor:pointer; }
      .wk-dialog-btn-ok:hover{ opacity:.9; }
      .wk-dialog-btn-ok:disabled{ opacity:.5; cursor:not-allowed; }
      .wk-dialog-msg{ margin:0 0 4px; font-size:14px; color:var(--muted); line-height:1.5; }
      .wk-dialog-btn-danger{ border:0; background:var(--danger); color:#fff; border-radius:10px; padding:9px 20px; font:inherit; font-size:14px; font-weight:700; cursor:pointer; }
      .wk-dialog-btn-danger:hover{ opacity:.9; }
      .wk-dialog-btn-danger:disabled{ opacity:.5; cursor:not-allowed; }

      /* modal de la compra */
      .wk-modal-scrim{ position:fixed; inset:0; z-index:60; background:rgba(25,18,12,.42); display:grid; place-items:center; padding:20px; animation:fade .15s ease; }
      @keyframes fade{ from{opacity:0} to{opacity:1} }
      .wk-modal{ width:440px; max-width:100%; max-height:80vh; background:var(--surface); border:1px solid var(--border); border-radius:18px; box-shadow:var(--shadow-card-lg); padding:18px; display:flex; flex-direction:column; }
      .wk-modal-hd{ display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:14px; }
      .wk-modal-list{ overflow-y:auto; display:flex; flex-direction:column; gap:4px; }
      .wk-shop-subtitle{ font-weight:600; font-size:13px; color:var(--muted); margin-top:2px; }
      .wk-shop-close{ width:38px; height:38px; border-radius:11px; border:0; background:transparent; color:var(--foreground); cursor:pointer; display:grid; place-items:center; transition:.12s; flex:none; }
      .wk-shop-close:hover{ background:var(--surface-secondary); }
      .wk-modal-shop{ width:860px; }
      .wk-shop-list{ display:block; columns:3; column-gap:36px; column-rule:1px solid var(--border); }
      .wk-shop-list .wk-shop-row{ break-inside:avoid; -webkit-column-break-inside:avoid; }
      @media(max-width:760px){
        .wk-modal-shop{ width:440px; }
        .wk-shop-list{ columns:1; column-rule:none; }
      }
      .wk-shop-row{ display:flex; align-items:center; gap:12px; padding:9px 8px; border-radius:9px; }
      .wk-shop-row:hover{ background:var(--surface-secondary); }
      .wk-shop-qty{ flex:0 0 62px; font-weight:800; font-size:13px; color:var(--accent-soft-foreground); font-variant-numeric:tabular-nums; }
      .wk-shop-name{ flex:1; font-weight:700; font-size:14px; }
      .wk-shop-del{ flex:none; width:34px; height:34px; border-radius:10px; border:0; background:transparent; color:var(--faint); display:grid; place-items:center; cursor:pointer; transition:.12s; }
      .wk-shop-row:hover .wk-shop-del{ color:var(--muted); }
      .wk-shop-del:hover{ background:var(--danger-soft); color:var(--danger); }
      .wk-shop-del:active{ transform:translateY(1px); }
      .wk-shop-empty{ padding:16px; text-align:center; color:var(--muted); font-weight:600; font-size:14px; }
      .wk-shop-footer-btn{ margin-top:14px; width:100%; display:flex; align-items:center; justify-content:center; gap:9px; border:0; background:var(--accent); color:#fff; border-radius:13px; padding:13px 20px; font:inherit; font-size:15px; font-weight:800; cursor:pointer; transition:.12s; }
      .wk-shop-footer-btn:hover:not(:disabled){ opacity:.9; }
      .wk-shop-footer-btn:disabled{ opacity:.45; cursor:not-allowed; }
      .wk-toast-act{ margin-left:14px; border:0; background:transparent; color:inherit; font:inherit; font-weight:800; text-decoration:underline; text-underline-offset:3px; cursor:pointer; padding:0; }
      .week-screen .wk-card.flash{ animation:wkFlash 1.5s ease; }
      @keyframes wkFlash{ 0%{ box-shadow:0 0 0 0 var(--accent); } 30%{ box-shadow:0 0 0 3px var(--accent-soft); background:var(--accent-soft); } 100%{ box-shadow:0 0 0 0 transparent; } }
      .wk-modal-lg{ width:600px; }
      .wk-rev-body{ overflow-y:auto; display:grid; grid-template-columns:1fr 1fr; gap:10px; }
      .wk-rev-card{ border:1px solid var(--border); border-radius:13px; padding:12px 14px; background:var(--surface-secondary); min-width:0; }
      .wk-rev-wide{ grid-column:1 / -1; }
      .wk-rev-warn{ border-color:color-mix(in oklab,var(--warning,#d97706) 40%, var(--border)); }
      .wk-rev-t{ font-size:11.5px; font-weight:800; text-transform:uppercase; letter-spacing:.04em; color:var(--muted); margin-bottom:8px; }
      .wk-rev-big{ font-size:28px; font-weight:800; line-height:1; }
      .wk-rev-big small{ font-size:13px; font-weight:700; color:var(--muted); margin-left:3px; }
      .wk-rev-bar{ height:7px; border-radius:99px; background:var(--border); margin-top:10px; overflow:hidden; }
      .wk-rev-bar i{ display:block; height:100%; background:var(--accent); border-radius:99px; }
      .wk-rev-note{ font-weight:700; font-size:12.5px; color:var(--muted); margin-top:8px; }
      .wk-rev-card > .wk-rev-note:first-of-type{ margin-top:0; }
      .wk-rev-list{ display:flex; flex-direction:column; gap:6px; }
      .wk-rev-row{ display:flex; align-items:center; gap:8px; font-size:13.5px; font-weight:600; }
      .wk-rev-row b{ font-weight:800; }
      .wk-rev-l{ flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .wk-rev-who{ font-size:13px; color:var(--muted); font-weight:700; }
      .wk-rev-chips{ display:flex; flex-wrap:wrap; gap:6px; }
      .wk-rev-chip{ font-size:12.5px; font-weight:700; padding:4px 9px; border-radius:8px; background:var(--surface); border:1px solid var(--border); }
      .wk-rev-chip.more{ color:var(--muted); }
      .wk-nutp-list{ display:flex; flex-direction:column; gap:12px; }
      .wk-nutp + .wk-nutp{ border-top:1px solid var(--border); padding-top:12px; }
      .wk-nutp-hd{ display:flex; align-items:center; gap:8px; margin-bottom:8px; font-size:14px; }
      .wk-nutp-av{ width:26px; height:26px; border-radius:99px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
      .wk-nutp-age{ font-size:12px; font-weight:700; color:var(--muted); }
      .wk-nutp-grid{ display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:8px; }
      .wk-nutp-m{ display:flex; gap:7px; align-items:flex-start; background:var(--surface); border:1px solid var(--border); border-radius:10px; padding:8px 9px; min-width:0; }
      .wk-nutp-dot{ width:9px; height:9px; border-radius:99px; margin-top:4px; flex-shrink:0; background:var(--border); }
      .wk-nutp-dot.ok{ background:var(--success,#16a34a); }
      .wk-nutp-dot.warn{ background:var(--warning,#d97706); }
      .wk-nutp-dot.bad{ background:var(--danger,#dc2626); }
      .wk-nutp-mt{ display:flex; flex-direction:column; min-width:0; }
      .wk-nutp-ml{ font-size:11px; font-weight:800; color:var(--muted); text-transform:uppercase; letter-spacing:.03em; }
      .wk-nutp-mt b{ font-size:15px; font-weight:800; }
      .wk-nutp-mh{ font-size:11.5px; font-weight:600; color:var(--muted); }
      .wk-nutp-notes{ font-size:12px; font-weight:600; color:var(--muted); margin-top:6px; }
      .wk-nutp-ai{ margin-top:12px; }
      .wk-nutp-ai-btn{ display:inline-flex; align-items:center; gap:6px; cursor:pointer; font:inherit; font-size:12.5px; font-weight:800; color:var(--foreground); }
      .wk-nutp-ai-btn:disabled{ opacity:.6; cursor:default; }
      .wk-nutp-ai-text{ white-space:pre-line; font-size:13.5px; font-weight:600; line-height:1.45; background:color-mix(in oklab,var(--accent) 7%, var(--surface)); border:1px solid color-mix(in oklab,var(--accent) 25%, var(--border)); border-radius:10px; padding:10px 12px; }
      .wk-nutp-fine{ font-size:11.5px; color:var(--muted); margin-top:10px; }
      @media(max-width:700px){ .wk-nutp-grid{ grid-template-columns:repeat(2,minmax(0,1fr)); } }
      .wk-rev-foot{ display:flex; gap:10px; margin-top:14px; }
      .wk-rev-btn{ flex:1; display:flex; align-items:center; justify-content:center; gap:8px; border:1px solid var(--border); background:var(--surface-secondary); color:var(--foreground); border-radius:13px; padding:12px 16px; font:inherit; font-size:14.5px; font-weight:800; cursor:pointer; transition:.12s; }
      .wk-rev-btn.primary{ background:var(--accent); border-color:var(--accent); color:#fff; }
      .wk-rev-btn:hover{ opacity:.9; }
      @media(max-width:560px){ .wk-rev-body{ grid-template-columns:1fr; } .wk-rev-foot{ flex-direction:column; } }
    `}</style>
  );
}
